import { stripBasePath, withBasePath } from '@/config/langPath';

/**
 * Quiz screen URLs: `/quiz/<slug>`. The quiz stays one DivKit card under the `/*` route; the host mirrors
 * `currentPage` into the address bar with raw history calls (the router would remount the page), so
 * analytics sees one URL per screen and browser Back can switch screens.
 */
export const QUIZ_ROUTE_PREFIX = '/quiz/';

/** `basePath` is the language prefix (`/ru`), kept in front of every screen URL. */
export const screenPath = (slug: string, basePath = ''): string => withBasePath(`${QUIZ_ROUTE_PREFIX}${slug}`, basePath);

/** Slug of a `[/<lang>]/quiz/<slug>` path, `null` for `/` and every other route. */
export const screenSlugFromPath = (pathname: string, basePath = ''): string | null => {
  const path = stripBasePath(pathname, basePath);
  if (!path.startsWith(QUIZ_ROUTE_PREFIX)) return null;
  const slug = path.slice(QUIZ_ROUTE_PREFIX.length).replace(/\/+$/, '');
  return slug || null;
};

export interface ScreenHistory {
  state: unknown;
  pushState: (state: unknown, title: string, url: string) => void;
  replaceState: (state: unknown, title: string, url: string) => void;
  back: () => void;
}
export interface ScreenLocation {
  pathname: string;
}

/** `@solidjs/router` keeps its Back/Forward delta in `history.state._depth`; keep it in step. */
const depthState = (state: unknown, delta: number): unknown => {
  const record = (state && typeof state === 'object' ? state : {}) as { _depth?: number };
  return typeof record._depth === 'number' ? { ...record, _depth: record._depth + delta } : state;
};

/**
 * Mirrors the quiz screen into the address bar and keeps a stack of what it pushed, so an in-layout
 * "back" becomes `history.back()` (no new entry) and browser Back lands on the previous screen only.
 * No query string: heatmaps group by URL, and per-user params would give one page per user.
 */
export class ScreenUrlSync {
  private stack: string[] = [];

  constructor(
    private readonly history: ScreenHistory,
    private readonly location: ScreenLocation,
    private readonly basePath = '',
  ) {}

  sync(slug: string): void {
    const path = screenPath(slug, this.basePath);
    if (this.location.pathname === path) {
      if (!this.stack.length) this.stack.push(slug);
      return;
    }
    try {
      if (!this.stack.length) {
        this.history.replaceState(depthState(this.history.state, 0), '', path);
        this.stack.push(slug);
        return;
      }
      if (this.stack.length >= 2 && this.stack[this.stack.length - 2] === slug) {
        this.stack.pop();
        this.history.back();
        return;
      }
      this.history.pushState(depthState(this.history.state, 1), '', path);
      this.stack.push(slug);
    } catch {
      // History API blocked: the screen still changes, only the URL stays.
    }
  }

  /** Browser Back/Forward landed on `slug`: cut the stack there (or extend it on Forward past our entries). */
  onPopState(slug: string): void {
    const index = this.stack.lastIndexOf(slug);
    if (index >= 0) this.stack.length = index + 1;
    else this.stack.push(slug);
  }
}
